/**
 * useVisualViewportKeyboard — tracks iOS virtual keyboard height via visualViewport.
 * Updates CSS --keyboard-offset on the inputBarContainer and adjusts threadArea padding.
 * Extracted from PrimateVisionV2.tsx (PRI-291 Phase 5)
 */
import { useEffect } from 'react'

export function useVisualViewportKeyboard(
  inputBarContainerRef: React.RefObject<HTMLElement | null>,
  threadAreaRef: React.RefObject<HTMLElement | null>,
  isMobile: boolean,
) {
  useEffect(() => {
    if (!isMobile || typeof window === 'undefined' || !window.visualViewport) return
    const vv = window.visualViewport!
    const root = typeof document !== 'undefined' ? document.documentElement : null
    const handleResize = () => {
      const keyboardHeight = Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
      // Set --keyboard-offset on the root so ALL fixed children (results frame, input bar)
      // can inherit it via var(). Also set on the input bar element directly for backwards
      // compatibility with any inline style that reads it from a non-inheriting context.
      if (root) root.style.setProperty('--keyboard-offset', keyboardHeight + 'px')
      if (inputBarContainerRef.current) inputBarContainerRef.current.style.setProperty('--keyboard-offset', keyboardHeight + 'px')
      if (threadAreaRef.current) threadAreaRef.current.style.paddingBottom = `calc(${68 + 49 + keyboardHeight + 16}px + env(safe-area-inset-bottom, 0px))`
      // PRI-498: iOS Safari scrolls thread-area when the textarea is focused (nearest
      // scrollable ancestor scroll). After keyboard close the scrollTop persists making
      // the results frame appear blank. Reset it once the keyboard is fully gone.
      if (keyboardHeight === 0 && threadAreaRef.current) {
        threadAreaRef.current.scrollTop = 0
      }
      // PRI-328 (real-device follow-up to 4d2dc65): on iOS Safari with body-scroll-lock,
      // the layout viewport can't scroll, but the *visual* viewport still gets pushed
      // down by `vv.offsetTop` when the keyboard opens so the focused input stays visible.
      // Our position:fixed camera zone is anchored to the layout viewport (top=0), so it
      // ends up ABOVE the visible region. Propagating vv.offsetTop into --pv2-vv-offset
      // lets the camera + thread-area `top:` calc tracks the visual viewport.
      if (root) {
        root.style.setProperty('--pv2-vv-offset', vv.offsetTop + 'px')
        root.style.setProperty('--pv2-vv-height', vv.height + 'px')
      }
    }
    handleResize() // prime values on mount
    vv.addEventListener('resize', handleResize)
    vv.addEventListener('scroll', handleResize)
    return () => {
      vv.removeEventListener('resize', handleResize)
      vv.removeEventListener('scroll', handleResize)
      if (root) {
        root.style.setProperty('--pv2-vv-offset', '0px')
        root.style.setProperty('--keyboard-offset', '0px')
      }
    }
  }, [isMobile]) // eslint-disable-line react-hooks/exhaustive-deps
}
